/**
 * One "existing integration" row of the migrate wizard: label + input + Validate button + the
 * verdict. The verdict is pinned to the exact text that was validated — editing the field resets it
 * (the parent clears the stored validation), so a stale "Found" can never be submitted.
 */
import { CheckCircle2, Loader2, XCircle, AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { TOOL_META, type MigrationTool, type ToolValidation } from "@/lib/migration";

export interface IntegrationFieldProps {
  tool: MigrationTool;
  value: string;
  onChange: (value: string) => void;
  onValidate: () => void;
  validation?: ToolValidation;
  /** Step-level error (e.g. "Validate … before continuing"). */
  error?: string;
  /** §12.1 — field shown read-only with an explanation when the role may not link this tool. */
  locked?: boolean;
}

export function IntegrationField({
  tool,
  value,
  onChange,
  onValidate,
  validation,
  error,
  locked = false,
}: IntegrationFieldProps): JSX.Element {
  const meta = TOOL_META[tool];
  const id = `mig-${tool}`;
  const helpId = `${id}-help`;
  const resultId = `${id}-result`;
  const errorId = `${id}-error`;
  const checking = validation?.status === "checking";
  const current = validation?.validatedValue === value.trim();
  const result = current ? validation?.result : undefined;
  const requestError = current ? validation?.error : undefined;

  const describedBy = [helpId, result || requestError ? resultId : null, error ? errorId : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {meta.label}{" "}
        <span className="font-normal text-text-secondary">(optional)</span>
      </Label>
      <div className="flex gap-2">
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={meta.placeholder}
          disabled={locked}
          aria-invalid={error || (result && !result.valid) ? true : undefined}
          aria-describedby={describedBy}
          autoComplete="off"
          spellCheck={false}
        />
        <Button
          type="button"
          variant="outline"
          onClick={onValidate}
          disabled={locked || checking || value.trim() === ""}
          aria-label={`Validate ${meta.label}`}
        >
          {checking ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : null}
          Validate
        </Button>
      </div>
      <p id={helpId} className="text-xs text-text-secondary">
        {locked
          ? "Only admin or leadership can link an existing GitHub repo or Slack channel."
          : meta.help}
      </p>
      <div id={resultId} role="status" aria-live="polite">
        {checking && (
          <p className="text-xs text-text-secondary">Checking…</p>
        )}
        {!checking && result && (
          <p
            className={cn(
              "flex items-start gap-1.5 text-xs",
              result.valid ? "text-success" : "text-error",
            )}
          >
            {result.valid ? (
              result.checked ? (
                <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              ) : (
                <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              )
            ) : (
              <XCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            )}
            <span>
              <span className="sr-only">{result.valid ? "Valid: " : "Not valid: "}</span>
              {result.message}
            </span>
          </p>
        )}
        {!checking && requestError && (
          <p className="flex items-start gap-1.5 text-xs text-error">
            <XCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{requestError}</span>
          </p>
        )}
      </div>
      {error && (
        <p id={errorId} role="alert" className="text-sm text-error">
          {error}
        </p>
      )}
    </div>
  );
}

export default IntegrationField;
